import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface VideoModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  videoSrc?: string;
  isYoutube?: boolean;
  width?: number;
}

const VideoModal: React.FC<VideoModalProps> = ({
  open,
  handleClose,
  title = "Video Player",
  videoSrc = "",
  isYoutube = false,
  width = 800,
}) => {
  const [VideoURL, setVideoURL] = useState("");

  useEffect(() => {
    if (isYoutube) {
      const videoId = isYoutube ? getYouTubeVideoId(videoSrc) : null;
      const finalVideoSrc = videoId
        ? `https://www.youtube.com/embed/${videoId}?autoplay=1`
        : "";
      setVideoURL(finalVideoSrc);
    } else {
      setVideoURL(videoSrc);
    }
  }, [videoSrc]);

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/]+)/
    );
    return match ? match[1] : null;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "90%",
            sm: "80%",
            md: width,
          },
          maxHeight: "90vh",
          bgcolor: "#393939",
          boxShadow: 24,
          p: 1,
          color: "#ffffff",
          borderRadius: 0,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ p: 1, pl: 2, pt: 3, fontSize: "16px", fontWeight: "600" }}
          >
            {title}
          </Typography>
          <IconButton
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
            onClick={handleClose}
          >
            <CloseIcon
              sx={{
                color: "#E12013",
                position: "relative",
                top: -8,
                right: -3,
              }}
            />
          </IconButton>
        </Box>

        {/* Video Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            height: "60vh",
          }}
        >
          {isYoutube ? (
            VideoURL ? (
              <iframe
                width="100%"
                height="80%"
                src={VideoURL}
                title="YouTube video"
                style={{ border: "none" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <Typography>
                The provided URL is not a valid video. You can view it{" "}
                <a
                  href={videoSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#DAAA00",
                    textDecoration: "underline",
                  }}
                >
                  here
                </a>
                .
              </Typography>
            )
          ) : VideoURL ? (
            <video width="100%" height="80%" controls autoPlay muted>
              <source src={VideoURL} type="video/mp4" />
            </video>
          ) : (
            <Typography color="error">No video source provided</Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default VideoModal;
